import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService
} from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehiclePaymentsFrequency,
  ReplaceVehicleQuickQuoteContext,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';

@Component({
  selector: 'agent-help',
  templateUrl: './agent-help.component.html',
  styleUrls: ['./agent-help.component.scss'],
  standalone: false
})
export class AgentHelpComponent implements OnInit, OnDestroy, AfterViewInit {
  public exitFlowModalId = this.modalService.createModalId('exitFlowModalId');
  newRiskDescription$: Observable<any>;
  errorOnExceptionANS$: Observable<boolean>;
  private stop$ = new Subject<void>();
  errors: Array<ErrorObj> = [];
  context = '';
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  analyticsEventType: AnalyticsEventType;
  paymentFrequency: ReplaceVehiclePaymentsFrequency;
  operation: ReplaceVehicleQuickQuoteOperation;

  constructor(
    private router: Router,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private spinnerService: LoadingSpinnerService,
    private copyService: CopyService,
    private dockingBarService: DockingBarService,
    private store: Store,
    private modalService: DsModalService
  ) {
    this.analyticsEventType =
      this.router.getCurrentNavigation()?.extras?.state?.analyticsEventType ?? '';
    this.paymentFrequency =
      this.router.getCurrentNavigation()?.extras?.state?.paymentFrequency ?? '';
    this.operation = this.router.getCurrentNavigation()?.extras?.state?.operation ?? '';
  }

  ngAfterViewInit(): void {
    this.quickQuoteFeatureFacade.context$.pipe(take(1)).subscribe(context => {
      this.context = context;
      if (this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT) {
        this.quickQuoteFeatureFacade.sendAnalytics(
          AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT
        );
      } else if (
        this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT
      ) {
        this.quickQuoteFeatureFacade.sendAnalytics(
          AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT,
          '',
          '',
          this.paymentFrequency
        );
      } else if (this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR) {
        this.quickQuoteFeatureFacade.sendAnalytics(
          AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR
        );
      } else if (context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE) {
        this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_AGENT_HELP);
      } else {
        this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.QUICK_QUOTE_AGENT_HELP);
      }
    });
  }

  ngOnInit(): void {
    this.dockingBarService.registerHeading('QUICK QUOTE');
    this.newRiskDescription$ = this.quickQuoteFeatureFacade.newRiskDescription$;
    this.errorOnExceptionANS$ = this.quickQuoteFeatureFacade.errorOnExceptionANS$;
    this.handleSpinnerOnError();
    this.setError();
  }

  sendToAgent(isCancelEvent = false) {
    this.spinnerService.start({ blockActions: true });

    if (this.context === ReplaceVehicleQuickQuoteContext.REPLACE_VEHICLE) {
      this.quickQuoteFeatureFacade.setOperation(
        this.operation || ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_SEND_TO_AGENT_BIND
      );
      this.quickQuoteFeatureFacade.sendToAgentOnCancel();

      if (this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT) {
        const analyticsType = isCancelEvent
          ? AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT_CANCEL_SEND_TO_AGENT
          : AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT_SENT_TO_AGENT;
        this.quickQuoteFeatureFacade.sendAnalytics(analyticsType);
      } else if (
        this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT
      ) {
        const analyticsType = isCancelEvent
          ? AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT_CANCEL_SENT_TO_AGENT
          : AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT_SENT_TO_AGENT;
        this.quickQuoteFeatureFacade.sendAnalytics(analyticsType, '', '', this.paymentFrequency);
      } else if (this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR) {
        this.quickQuoteFeatureFacade.sendAnalytics(
          AnalyticsEventType.REPLACE_VEHICLE_ONLINE_BIND_ERROR_SEND_TO_AGENT
        );
      }
    } else {
      this.quickQuoteFeatureFacade.sendToAgentOnError();
      this.quickQuoteFeatureFacade.sendAnalytics(
        AnalyticsEventType.BUTTON_AGENT_HELP_SEND_TO_AGENT
      );
    }
  }

  handleSpinnerOnError() {
    this.errorOnExceptionANS$
      .pipe(
        filter(error => !!error),
        takeUntil(this.stop$),
        tap(() => this.spinnerService.stop())
      )
      .subscribe();
  }

  setError() {
    this.errors = [];
    this.errors.push(<ErrorObj>{
      errorLevel: ErrorLevels.HIGH,
      errorMessage: this.copyService.getCopy('policy.quickQuoteErrorMessage')
    });
  }

  cancel() {
    this.modalService.open(this.exitFlowModalId);
    if (this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT) {
      this.quickQuoteFeatureFacade.sendAnalytics(
        AnalyticsEventType.REPLACE_VEHICLE_QUOTE_SPINNER_NEED_AGENT_CANCEL
      );
    } else if (
      this.analyticsEventType === AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT
    ) {
      this.quickQuoteFeatureFacade.sendAnalytics(
        AnalyticsEventType.REPLACE_VEHICLE_FUTURE_PAYMENTS_NEED_AGENT_CANCEL,
        '',
        '',
        this.paymentFrequency
      );
    }
  }

  stay() {
    this.modalService.close(this.exitFlowModalId);
  }

  goBack() {
    this.store.dispatch(fromRouterActions.Back());
  }

  leave() {
    this.store.dispatch(fromRouterActions.Go({ path: ['overview'] }));
  }

  ngOnDestroy() {
    this.spinnerService.stop();
    this.stop$.next();
    this.stop$.complete();
  }
}
